import { Plus } from 'lucide-react';
import { useState } from 'react';

import type { Persona } from '@repo/contracts';
import { Tooltip } from '@repo/ui/tooltip';

import { PersonaAvatar } from '../persona/persona-avatar';

/** The gap between one face arriving and the next, on first appearance. */
const STAGGER_MS = 70;

/** Arriving one after another, each a little after the last: the panel
 *  taking its seats, not appearing all at once. */
const ARRIVE =
  'duration-400 animate-in fade-in zoom-in-75 slide-in-from-right-3 fill-mode-backwards ease-out motion-reduce:animate-none';

function first(name: string): string {
  return name.split(' ')[0] ?? name;
}

/**
 * Who is in the room, hanging off the panel's left edge over the board —
 * where it costs the conversation no height at all.
 *
 * A column of faces tucked against the panel. Hovering a face names it;
 * clicking opens their card. Who is speaking is the thread's to show — it
 * says who is typing — so the rail does not say it twice.
 */
export function PersonaRail({
  onManage,
  onPick,
  openId,
  personas,
}: {
  onManage?: () => void;
  onPick: (id: string) => void;
  openId: string | null;
  personas: Persona[];
}) {
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <>
      {personas.map((persona, index) => {
        const open = openId === persona.id;
        const named = hovered === persona.id;
        return (
          <div
            className={`relative flex justify-end ${ARRIVE}`}
            key={persona.id}
            style={{ animationDelay: `${String(index * STAGGER_MS)}ms` }}
          >
            {named ? (
              // Positioned by the wrapper, animated inside it: an entrance's
              // transform would replace the one that centres it.
              <span className="pointer-events-none absolute inset-y-0 right-full mr-2.5 flex items-center">
                <span className="flex items-center gap-1.5 rounded-pill bg-surface py-1 pr-2.5 pl-2.5 text-tag whitespace-nowrap shadow-popover ring-1 ring-hairline duration-200 animate-in fade-in zoom-in-95 slide-in-from-right-2 motion-reduce:animate-none">
                  <span className="font-semibold text-text-primary">
                    {first(persona.name)}
                  </span>
                  <span className="text-text-muted">{persona.title}</span>
                </span>
              </span>
            ) : null}
            <button
              aria-expanded={open}
              aria-label={`${persona.name}, ${persona.title}`}
              className={[
                'relative cursor-pointer rounded-full transition-transform duration-300 ease-out motion-reduce:transition-none',
                'hover:-translate-x-1',
              ].join(' ')}
              onBlur={() => {
                setHovered(null);
              }}
              onClick={() => {
                onPick(persona.id);
              }}
              onFocus={() => {
                setHovered(persona.id);
              }}
              onMouseEnter={() => {
                setHovered(persona.id);
              }}
              onMouseLeave={() => {
                setHovered(null);
              }}
              type="button"
            >
              <PersonaAvatar
                className={[
                  'shadow-popover transition-shadow',
                  open ? 'ring-2 ring-ink' : 'ring-2 ring-surface',
                ].join(' ')}
                persona={persona}
                size="xl"
              />
            </button>
          </div>
        );
      })}
      {onManage ? (
        <Tooltip align="start" label="Change who is on the panel">
          <button
            style={{
              animationDelay: `${String(personas.length * STAGGER_MS)}ms`,
            }}
            aria-label="Manage the panel"
            className={`flex size-12 cursor-pointer items-center justify-center rounded-full bg-surface text-text-primary shadow-popover transition hover:-translate-x-1 hover:bg-surface-hover ${ARRIVE}`}
            onClick={onManage}
            type="button"
          >
            <Plus aria-hidden="true" className="size-5" />
          </button>
        </Tooltip>
      ) : null}
    </>
  );
}
